import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";

import type { UseProjectsReturn } from "@/hooks/use-projects";
import { getProjectChange, PROJECTS_CHANGED_EVENT } from "@/lib/project-events";

const ProjectsDataContext = createContext<UseProjectsReturn | null>(null);

interface ProjectsDataProviderProps {
  projects: UseProjectsReturn;
  children: ReactNode;
}

/**
 * Makes the shell's project list available to the sidebar, Projects workspace,
 * and keyboard shortcuts without making each surface fetch its own catalog.
 *
 * Existing project screens already publish confirmed local mutations through
 * `PROJECTS_CHANGED_EVENT`; consume those events here so callers that still
 * own a lightweight local hook stay in sync with the shell snapshot too.
 */
export function ProjectsDataProvider({ projects, children }: ProjectsDataProviderProps) {
  useEffect(() => {
    const handleProjectsChanged = (event: Event) => {
      const change = getProjectChange(event);
      if (!change) {
        void projects.refresh();
        return;
      }

      if (change.type === "remove") {
        projects.remove(change.projectId);
      } else {
        projects.upsert(change.project);
      }
    };

    window.addEventListener(PROJECTS_CHANGED_EVENT, handleProjectsChanged);
    return () => window.removeEventListener(PROJECTS_CHANGED_EVENT, handleProjectsChanged);
  }, [projects.refresh, projects.remove, projects.upsert]);

  return (
    <ProjectsDataContext.Provider value={projects}>
      {children}
    </ProjectsDataContext.Provider>
  );
}

/**
 * Null outside the main shell so isolated component tests and embedded views
 * can retain their existing local `useProjects` fallback.
 */
export function useSharedProjects(): UseProjectsReturn | null {
  return useContext(ProjectsDataContext);
}
